import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { OccasionalSlotsPlaces, RegularCancellationRequest } from '@/types/Slots'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import {
    ArrowForward,
    CalendarMonth,
    Check,
    ChildCareOutlined,
    Close,
    DoDisturbOff,
    Message,
    Person,
    Place,
    Update
} from '@mui/icons-material'
import moment from 'moment/moment'
import styles from './showRegularRequestDialog.module.scss'
import { Daycare, DaycareType } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import CancelRegularRequest from '../../regularRequest/cancelRegularRequest/cancelRegularRequest'
import ConfirmRegularRequest from '../../regularRequest/confirmRegularRequest/confirmRegularRequest'
import { ChildWithParents } from '@/types/Child'
import { Role } from '@/types/Role'
import { useAuth } from '@/modules/context/AuthContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { isPastDate, titleWithPastLabel } from '@/modules/helper/editionLimitHelper'

interface ShowRegularRequestDialogProps {
    open: boolean
    handleClose: () => void
    regularRequests: RegularCancellationRequest[]
    daycare: Daycare
    isParent: boolean
    refetchData: () => void
    childs: ChildWithParents[]
    canEditRegular: boolean
    places: OccasionalSlotsPlaces[]
}

const ShowRegularRequestDialog: FunctionComponent<ShowRegularRequestDialogProps> = ({
    open,
    handleClose,
    regularRequests,
    daycare,
    isParent,
    refetchData,
    childs,
    canEditRegular,
    places
}: ShowRegularRequestDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const regularRequest: RegularCancellationRequest = regularRequests[0]

    const isRequestAbsence: boolean = regularRequest
        ? moment(regularRequest.start, 'HH:mm:ss').format('HH:mm') ===
              moment(regularRequest.regularSlotStart, 'HH:mm:ss').format('HH:mm') &&
          moment(regularRequest.end, 'HH:mm:ss').format('HH:mm') ===
              moment(regularRequest.regularSlotEnd, 'HH:mm:ss').format('HH:mm')
        : false

    const [isCancelRegularRequestDialogOpen, setCancelRegularRequestDialogOpen] = useState(false)
    const [accepted, setAccepted] = useState(false)
    const [confirmRegularRequestDialogOpen, setConfirmRegularRequestDialogOpen] = useState(false)

    function handleAcceptRequestSlot(): void {
        setAccepted(true)
        setConfirmRegularRequestDialogOpen(true)
    }

    function handleRefuseRequestSlot(): void {
        setAccepted(false)
        setConfirmRegularRequestDialogOpen(true)
    }

    return (
        (regularRequests.length > 0 && (
            <>
                <Dialog open={open} handleClose={handleClose}>
                    <DialogHeader
                        title={titleWithPastLabel(
                            isRequestAbsence ? t('absenceRequest') : t('regularEditRequest'),
                            isPastDate(regularRequest.start),
                            t
                        )}
                        parent={isParent}
                    />
                    <DialogBody className={styles.DialogBody}>
                        <div className={styles.Container}>
                            <CalendarMonth
                                className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                            />
                            <div className={styles.TextContainer}>
                                <p className={styles.FirstText}>
                                    {format(parse(regularRequest.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                        locale: getLocalFromLanguage()
                                    })}
                                </p>
                                <p className={styles.SecondText}>
                                    {t('from') +
                                        ' ' +
                                        moment(regularRequest.start, 'HH:mm:ss').format('HH:mm') +
                                        ' ' +
                                        t('to').toLowerCase() +
                                        ' ' +
                                        moment(regularRequest.end, 'HH:mm:ss').format('HH:mm')}
                                </p>
                            </div>
                        </div>
                        <div className={styles.BreakLine}></div>
                        {isRpeOrLaep && user?.role !== Role.parent && regularRequest.parentName && (
                            <div className={styles.SecondContainer}>
                                <Person
                                    className={`${styles.Icon} ${styles.ClickableIconDaycare}`}
                                    onClick={() => navigate(`/adults?parent=${regularRequest.parentId}`)}
                                />
                                <p className={styles.Text}>{regularRequest.parentName}</p>
                            </div>
                        )}
                        {regularRequests.length > 0 &&
                            regularRequests.map((regularReq: RegularCancellationRequest, index: number) => (
                                <div className={styles.SecondContainer} key={index}>
                                    <ChildCareOutlined
                                        className={`${styles.Icon} ${
                                            isParent ? styles.IconParent : styles.ClickableIconDaycare
                                        }`}
                                        onClick={() => navigate(`/childs?child=${regularReq.childId}`)}
                                    />
                                    <p className={styles.Text}>
                                        {regularReq.childName +
                                            (regularReq.childComplement ? ' - ' + regularReq.childComplement : '')}
                                    </p>
                                </div>
                            ))}
                        {regularRequest.reason && (
                            <div className={styles.SecondContainer}>
                                <Message
                                    className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                                />
                                <p className={styles.Text}>{regularRequest.reason}</p>
                            </div>
                        )}
                        {!isRequestAbsence && (
                            <div className={styles.SecondContainer}>
                                <Update
                                    className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                                />
                                <ul className={styles.List}>
                                    <li className={styles.ListItemEditRegular}>
                                        <span className={styles.LeftText}>
                                            {moment(regularRequest.regularSlotStart, 'HH:mm:ss').format('HH:mm')}
                                        </span>
                                        <ArrowForward
                                            className={`${styles.Icon} ${
                                                isParent ? styles.IconParent : styles.IconDaycare
                                            }`}
                                        />
                                        <span className={styles.RightText}>
                                            {moment(regularRequest.start, 'HH:mm:ss').format('HH:mm')}
                                        </span>
                                    </li>
                                    <li className={styles.ListItemEditRegular}>
                                        <span className={styles.LeftText}>
                                            {moment(regularRequest.regularSlotEnd, 'HH:mm:ss').format('HH:mm')}
                                        </span>
                                        <ArrowForward
                                            className={`${styles.Icon} ${
                                                isParent ? styles.IconParent : styles.IconDaycare
                                            }`}
                                        />
                                        <span className={styles.RightText}>
                                            {moment(regularRequest.end, 'HH:mm:ss').format('HH:mm')}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        )}
                        {regularRequest.placeId && (
                            <>
                                {isRpeOrLaep && !isParent && <div className={styles.BreakLine}></div>}
                                <div className={styles.SecondContainer}>
                                    <Place
                                        className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                                    />
                                    <p className={styles.Text}>
                                        {regularRequest.placeName + ' ' + regularRequest.placeAddress}
                                    </p>
                                </div>
                            </>
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                onClick={handleClose}
                                parent={isParent}
                                className={styles.Button}
                            />
                            {isParent &&
                                moment(regularRequest.date, 'DD-MM-YYYY').isSameOrAfter(
                                    moment().subtract(1, 'd'),
                                    'd'
                                ) && (
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customText={t('cancel')}
                                        customIcon={<DoDisturbOff />}
                                        onClick={() => setCancelRegularRequestDialogOpen(true)}
                                        parent={isParent}
                                        className={styles.Button}
                                    />
                                )}
                            {!isParent &&
                                canEditRegular &&
                                moment(regularRequest.date, 'DD-MM-YYYY').isSameOrAfter(
                                    moment().subtract(3, 'months'),
                                    'd'
                                ) && (
                                    <>
                                        <ButtonTextIcon
                                            type={'custom'}
                                            onClick={handleAcceptRequestSlot}
                                            customIcon={<Check />}
                                            customText={t('accept')}
                                            className={styles.Button}
                                        />
                                        <ButtonTextIcon
                                            type={'custom'}
                                            onClick={handleRefuseRequestSlot}
                                            customIcon={<Close />}
                                            customText={t('refuse')}
                                            className={styles.Button}
                                        />
                                    </>
                                )}
                        </div>
                    </DialogBody>
                </Dialog>
                {isCancelRegularRequestDialogOpen && (
                    <CancelRegularRequest
                        handleClose={() => setCancelRegularRequestDialogOpen(false)}
                        refetchData={refetchData}
                        closeParentDialog={handleClose}
                        daycare={daycare}
                        regularRequests={regularRequests}
                    />
                )}
                {confirmRegularRequestDialogOpen && (
                    <ConfirmRegularRequest
                        places={places}
                        isRpeOrLaep={isRpeOrLaep}
                        open={confirmRegularRequestDialogOpen}
                        childs={childs}
                        handleClose={() => setConfirmRegularRequestDialogOpen(false)}
                        refetchData={refetchData}
                        closeParentDialog={handleClose}
                        regularRequests={regularRequests}
                        accepted={accepted}
                    />
                )}
            </>
        )) || <></>
    )
}

export default ShowRegularRequestDialog
